function lowercaseFirstLetter(value: string): string {
    return value.charAt(0).toLowerCase() + value.slice(1);
}

/**
 * Creates a new mock object for use on mock providers, or unit tests.
 * @param {T} obj Initial object from which mock will be created
 * @returns {T} Object with auto-generated methods `withPropertyName`
 * for each property `propertyName` of the initial object.
 * @note Will fail to create accessors for optional TypeScript properties
 * like `propertyName?: boolean` that will result in no property created in JavaScript.
 */
export function mockBuilder<T extends { [i: string | symbol]: any }>(
    obj: T,
): T & any {
    const proxy = new Proxy(obj, {
        get: (target: T, key: string | symbol) => {
            if (
                typeof key === 'string' &&
                key.startsWith('with') &&
                !(key in target)
            ) {
                // It's a builder property `withXxxx` we dynamically create the setter function
                let property = key.substring(4);

                property =
                    property in target
                        ? property
                        : lowercaseFirstLetter(property);
                if (property in target) {
                    return function (value: any) {
                        // Property is a simple value
                        (target as any)[property] = value;
                        return proxy;
                    };
                } else {
                    throw new Error(
                        `Property '${property}' doesn't exist for object ${
                            target.constructor.name || typeof target
                        }`,
                    );
                }
            }

            return target[key];
        },
    });

    return proxy;
}
